import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';

export interface ApplicationApiResponse {
  // Redirect responses
  redirectToSigner?: string;
  redirectToRecord?: string;

  // Form payload responses
  structure?: any;
  currentSignerIndex?: number;
  recordStatus?: string;
  allSigners?: any[];
  allSignersComplete?: boolean;
  currentSignerCompleted?: boolean;
  userId?: number;
  primaryCrmObject?: string;
  crmId?: string | null;
  brokerTrialEnded?: boolean;
}

export interface FetchApplicationDataParams {
  shortId: string;
  mappingShortId: string;
  recordId?: string;
  signerShortId?: string;
  pdfOnly?: boolean;
}

// Purpose: Fetch application data from the backend API
export const fetchApplicationData = async (
  params: FetchApplicationDataParams
): Promise<ApplicationApiResponse> => {
  const { shortId, mappingShortId, recordId, signerShortId, pdfOnly } = params;

  const logContext = {
    service: 'applicationApi',
    function: 'fetchApplicationData',
    shortId,
    mappingShortId,
    recordId,
    signerShortId,
  };

  logger.debug('Application API', 'Building endpoint URL', logContext);

  // Build the API endpoint dynamically based on available parameters
  let endpoint = `/api/record-actions/form/${shortId}/${mappingShortId}`;
  if (recordId) {
    endpoint += `/${recordId}`;
    if (signerShortId) {
      endpoint += `/${signerShortId}`;
    }
  }

  // Add query parameters if needed
  const queryParams = new URLSearchParams();
  if (pdfOnly) {
    queryParams.append('pdfOnly', '1');
  }
  
  const fullEndpoint = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

  logger.debug('Application API', 'Making API request', {
    ...logContext,
    endpoint: fullEndpoint,
    pdfOnly,
  });

  try {
    const response = await api.get<ApplicationApiResponse>(fullEndpoint);

    logger.debug('Application API', 'API response received', {
      ...logContext,
      endpoint,
      hasRedirectToSigner: !!response.data.redirectToSigner,
      hasRedirectToRecord: !!response.data.redirectToRecord,
      hasStructure: !!response.data.structure,
    });

    return response.data;
  } catch (error) {
    logger.error('Application API', 'API request failed', {
      ...logContext,
      endpoint,
      error,
    });
    throw error;
  }
};

/**
 * Fetches mapping information by short ID for application form setup.
 * @param userShortId The short ID of the user.
 * @param mappingShortId The short ID of the mapping.
 * @returns The mapping information needed for application setup.
 */
export const fetchMappingInfoByShortId = async (
  userShortId: string,
  mappingShortId: string
): Promise<{ primaryCrmObject: string; userId: number; mappingName: string }> => {
  const logContext = {
    service: 'applicationApi',
    function: 'fetchMappingInfoByShortId',
    userShortId,
    mappingShortId,
  };

  logger.debug('Application API', 'Fetching mapping info by short ID', logContext);

  try {
    const response = await api.get<{
      primaryCrmObject: string;
      userId: number;
      mappingName: string;
    }>(`/api/field-mapping/validate-mapping/${userShortId}/${mappingShortId}`);

    logger.debug('Application API', 'Mapping info fetched successfully', {
      ...logContext,
      primaryCrmObject: response.data.primaryCrmObject,
      userId: response.data.userId,
    });

    return response.data;
  } catch (error) {
    logger.error('Application API', 'Failed to fetch mapping info', {
      ...logContext,
      error,
    });
    throw error;
  }
};
